/**
 * video-orientation.js — window.VideoOrientation.
 *
 * ЗАЧЕМ ЭТОТ ФАЙЛ СУЩЕСТВУЕТ
 * --------------------------
 * У видео две системы координат, и наш код говорил на обеих сразу:
 *
 *   • <video> / HTMLVideoElement говорит в DISPLAY-координатах. Браузер сам
 *     применяет матрицу поворота из tkhd, поэтому videoWidth/videoHeight у
 *     портретной съёмки с телефона — 1080x1920, и drawImage(videoEl, …) рисует
 *     кадр стоймя.
 *   • VideoDecoder / VideoFrame говорит в CODED-координатах. Матрицу он не
 *     знает и знать не обязан: отдаёт ровно те пиксели, что лежат в потоке —
 *     1920x1080, лёжа на боку.
 *
 * Пока у инструмента был ОДИН движок, расхождение не было видно. Но почти у
 * каждого нашего видео-тула их ДВА: быстрый лейн на WebCodecs и запасной на
 * <video>/FFmpeg. И тогда один и тот же файл выходит стоймя или боком в
 * зависимости от того, какой лейн выиграл — превью правильное, результат
 * повёрнут. Именно так это и приходит в поддержку: «почему видео переворачивает
 * изображение и делает его горизонтально» (#7108, 06.09.2026, Android/Yandex;
 * до этого — комментарий #370 и «Фото, которые загрузились боком» на
 * /slideshow-maker).
 *
 * Чинить это в туле нельзя: замер 06.09.2026 нашёл ЧЕТЫРЕ независимые копии
 * чтения матрицы (ffmpeg-processor, video-merge-webcodecs, webcodecs-pipeline,
 * reverse-webcodecs) и при этом полтора десятка мест, которые рисуют
 * декодированный кадр вообще без единой мысли о повороте. Поворот, который надо
 * ПОМНИТЬ, забудут — он невидим на любом ландшафтном тестовом файле и виден
 * только на съёмке с телефона.
 *
 * ПОЭТОМУ здесь один ответ на три вопроса — «сколько градусов», «какого размера
 * кадр на самом деле» и «как его нарисовать», — а лейны отдают наружу кадры,
 * УЖЕ несущие свою ориентацию.
 *
 * ЗАМЕРЫ (06.09.2026, 1080p rot90, 100 кадров, эта машина)
 * -------------------------------------------------------
 *   new VideoFrame(f,{rotation})  Chromium: 0.002 мс/кадр — это метаданные,
 *                                 пикселей не касается; displayWidth/Height
 *                                 меняются местами, и ЛЮБОЙ существующий
 *                                 drawImage(frame) становится правильным сам.
 *                                 Firefox 148: молча ИГНОРИРУЕТ (dw/dh не
 *                                 меняются, .rotation === undefined).
 *   копия через canvas            Chromium 14.4 мс/кадр, Firefox 29.5 — то есть
 *                                 «копировать всегда» стоило бы Firefox-у ~30 мс
 *                                 на каждый кадр рендера.
 *   поворот внутри самого draw    ≈ цена обычного drawImage (20.3 против 20.1 мс
 *                                 у нативной обёртки) — то есть бесплатно.
 *
 * Отсюда стратегия: где браузер умеет нести поворот в кадре — несём (91 %
 * трафика этих тулов: yandex+chrome+edge+opera+samsung), и тогда старые места
 * вызова правильны без единой правки. Где не умеет (Firefox) — поворот едет
 * РЯДОМ с кадром (pending) и применяется в момент рисования draw/drawSource.
 *
 * ⚠️ 20.10.2026: «рядом» верно ТОЛЬКО у того, кто рисует через этот файл.
 * Экспорт /slideshow-maker, /clone-effect и фон /video-overlay рисовали кадр
 * обычным drawImage — и в Firefox клип с телефона уходил в файл боком
 * (обращение 19.10: колода 9:16, «последние видео стали в формате 16:9», в
 * превью — `<video>` — всё верно). Поэтому кадр, уходящий из ClipCursor к
 * потребителю, поворачивается по-настоящему — upright(), копия только у
 * кадра с неприменённым поворотом и только при выдаче (замер: 16 мс/кадр у
 * draw против 34 мс у копии, Firefox 148, 1080p). Потребитель, рисующий
 * через draw/drawSource/size, просит `deferRotation` и копии не платит —
 * объявившись в VideoDecodeLanesDeclareOrientationTest. WebKit (Safari 16.4+)
 * VideoDecoder имеет; несёт ли он поворот в кадре — решает native() на месте,
 * и дверь верна в обоих случаях.
 *
 * ⚠️ Поддержку проверяем НАСТОЯЩИМ круговым прогоном (собрали кадр → обернули →
 * потребовали И перестановки размеров, И что пиксель реально переехал), а не
 * наличием ключа в словаре и не строкой UA: Firefox принимает {rotation:90} без
 * ошибки и возвращает неповёрнутый кадр — конструктор, который «сработал»,
 * здесь не доказывает ничего.
 *
 * API
 * ---
 *   VideoOrientation.fromTkhd(mp4boxFile, trackId) → 0|90|180|270
 *   VideoOrientation.display(w, h, deg)            → {w, h} в display-координатах
 *   VideoOrientation.native()                      → умеет ли браузер нести поворот в кадре
 *   VideoOrientation.apply(frame, deg)             → кадр, несущий свою ориентацию
 *   VideoOrientation.pending(frame)                → сколько градусов ЕЩЁ применить при рисовании
 *   VideoOrientation.size(frame)                   → {w, h} — display-размер кадра, на любом движке
 *   VideoOrientation.draw(ctx, frame, dx, dy, dw, dh)
 *   VideoOrientation.drawSource(ctx, frame, sx, sy, sw, sh, dx, dy, dw, dh)
 *        sx/sy/sw/sh — в DISPLAY-координатах кадра (в тех, что видит пользователь).
 *   VideoOrientation.upright(frame)                → кадр с применённым поворотом
 *        (копия, только если pending; исходный закрывается) — для обычного drawImage.
 */
(function () {
'use strict';
if (window.VideoOrientation) return;

/* Поворот, который ещё не применён к пикселям и не унесён в сам кадр.
   WeakMap, а не свойство на объекте: VideoFrame — host-объект, expando на нём
   живёт, но у clone() его уже нет, а тихо потерянное свойство — ровно тот класс
   ошибки, который этот файл закрывает. Здесь потеря хотя бы честная: pending()
   вернёт 0, и кадр нарисуется как есть, а не как попало. */
var PENDING = new WeakMap();

function norm(deg) {
    var d = parseInt(deg, 10) || 0;
    d = ((d % 360) + 360) % 360;
    return (d === 90 || d === 180 || d === 270) ? d : 0;
}

/* ───────── чтение матрицы tkhd ─────────
   mp4 хранит матрицу 3x3 в фиксированной точке (16.16 для элементов 0-1,3-4,6-7
   и 2.30 для 2,5,8). Поворот сидит в верхнем левом 2x2:
       0°:  a=1   b=0        90°:  a=0  b=1
     180°:  a=-1  b=0       270°:  a=0  b=-1
   Всё, что не одна из четырёх осевых форм (зеркало фронтальной камеры, сдвиг),
   даёт 0: угадывать экзотику хуже, чем честно не трогать. */
function fromTkhd(mp4, trackId) {
    try {
        var trak = (mp4 && typeof mp4.getTrackById === 'function') ? mp4.getTrackById(trackId) : null;
        var m = trak && trak.tkhd && trak.tkhd.matrix;
        if (!m || m.length < 5) return 0;
        var a = m[0] / 65536, b = m[1] / 65536;
        if (Math.abs(a - 1) < 0.01 && Math.abs(b) < 0.01) return 0;
        if (Math.abs(a) < 0.01 && Math.abs(b - 1) < 0.01) return 90;
        if (Math.abs(a + 1) < 0.01 && Math.abs(b) < 0.01) return 180;
        if (Math.abs(a) < 0.01 && Math.abs(b + 1) < 0.01) return 270;
    } catch (e) {}
    return 0;
}

function display(w, h, deg) {
    var d = norm(deg);
    return (d === 90 || d === 270) ? { w: h || 0, h: w || 0 } : { w: w || 0, h: h || 0 };
}

var _native = null;
function native() {
    if (_native !== null) return _native;
    _native = false;
    try {
        if (typeof VideoFrame === 'undefined') return _native;
        var C, cx;
        if (window.OffscreenCanvas) { C = new OffscreenCanvas(64, 32); }
        else { C = document.createElement('canvas'); C.width = 64; C.height = 32; }
        cx = C.getContext('2d');
        if (!cx) return _native;
        cx.fillStyle = '#ff0000'; cx.fillRect(0, 0, 32, 32);
        cx.fillStyle = '#0000ff'; cx.fillRect(32, 0, 32, 32);
        var base = new VideoFrame(C, { timestamp: 0 });
        var wrapped = null;
        try {
            wrapped = new VideoFrame(base, { rotation: 90 });
            if (wrapped.displayWidth === 32 && wrapped.displayHeight === 64) {
                var t = document.createElement('canvas');
                t.width = 32; t.height = 64;
                var tx = t.getContext('2d', { willReadFrequently: true });
                tx.drawImage(wrapped, 0, 0);
                var top = tx.getImageData(16, 6, 1, 1).data;
                var bot = tx.getImageData(16, 58, 1, 1).data;
                // +90° по часовой: левая (красная) половина уезжает ВВЕРХ.
                _native = (top[0] > 150 && top[2] < 100 && bot[2] > 150 && bot[0] < 100);
            }
        } catch (e) { _native = false; }
        try { if (wrapped) wrapped.close(); } catch (e) {}
        try { base.close(); } catch (e) {}
    } catch (e) { _native = false; }
    return _native;
}

/* Отдать кадр, несущий свою ориентацию.
   ⚠️ Владение: при нативной обёртке ИСХОДНЫЙ кадр закрывается здесь — наружу
   уходит ровно один объект, и close() на нём по-прежнему обязан звать caller. */
function apply(frame, deg) {
    var d = norm(deg);
    if (!frame || !d) return frame;
    if (native()) {
        try {
            var wrapped = new VideoFrame(frame, { rotation: d });
            try { frame.close(); } catch (e) {}
            return wrapped;
        } catch (e) { /* уходим в ветку «поворот едет рядом» */ }
    }
    try { PENDING.set(frame, d); } catch (e) {}
    return frame;
}

function pending(frame) {
    if (!frame) return 0;
    try { return PENDING.get(frame) || 0; } catch (e) { return 0; }
}

/* display-размер кадра на ЛЮБОМ движке: на нативном его несёт сам кадр, на
   остальных — считаем из ещё не применённого поворота. Работает и для
   HTMLVideoElement/ImageBitmap, чтобы вызывающему не приходилось разбираться,
   что именно ему подсунули. */
function size(frame) {
    if (!frame) return { w: 0, h: 0 };
    /* Порядок важен: у <img> есть И naturalWidth (пиксели файла), И width
       (размер вёрстки) — и второе врёт, как только у картинки есть CSS. Кадры и
       <video> отвечают на первые три ключа, картинка — на naturalWidth. */
    var w = frame.displayWidth || frame.codedWidth || frame.videoWidth || frame.naturalWidth || frame.width || 0;
    var h = frame.displayHeight || frame.codedHeight || frame.videoHeight || frame.naturalHeight || frame.height || 0;
    return display(w, h, pending(frame));
}

/* HDR-кадр (HLG/PQ, iPhone с iOS 13) — через дверь HDR → SDR (hdr-to-sdr.js): собственная 2D-картинка браузера
   у него тусклая (опорный белый ~70 % белого SDR), а это она уходила в кадры превью, нарезку и результат
   (замер и выбор цели — docs/hdr-to-sdr.md). Дверь отдаёт холст того же размера и ориентации, что и drawImage
   кадра, или сам кадр, если он SDR. Двери на странице ещё нет — просим её один раз и рисуем как раньше. */
var _doorAsked = false;
function sdr(frame) {
    var H = window.HdrToSdr;
    if (H) return H.image(frame);
    var cs = null;
    try { cs = frame && frame.colorSpace; } catch (e) {}
    if (cs && !_doorAsked && (cs.transfer === 'hlg' || cs.transfer === 'pq' || cs.primaries === 'bt2020')
        && typeof document !== 'undefined' && typeof window.__jsSrc === 'function') {
        _doorAsked = true;
        try {
            var sc = document.createElement('script');
            sc.src = window.__jsSrc('/js/hdr-to-sdr.js');
            document.head.appendChild(sc);
        } catch (e) {}
    }
    return frame;
}

/* Нарисовать кадр в display-прямоугольник (dx,dy,dw,dh). Если поворот уже в
   кадре — это обычный drawImage. Если едет рядом — тот же drawImage под
   трансформом; замер показал, что трансформ стоит столько же, сколько его
   отсутствие. */
function draw(ctx, frame, dx, dy, dw, dh) {
    if (!ctx || !frame) return;
    if (dx == null) { var s = size(frame); dx = 0; dy = 0; dw = s.w; dh = s.h; }
    var d = pending(frame);
    if (!d) { ctx.drawImage(sdr(frame), dx, dy, dw, dh); return; }
    ctx.save();
    ctx.translate(dx, dy);
    if (d === 90) { ctx.translate(dw, 0); ctx.rotate(Math.PI / 2); }
    else if (d === 180) { ctx.translate(dw, dh); ctx.rotate(Math.PI); }
    else { ctx.translate(0, dh); ctx.rotate(-Math.PI / 2); }
    // после поворота на 90/270 оси прямоугольника меняются местами
    var rw = (d === 180) ? dw : dh, rh = (d === 180) ? dh : dw;
    ctx.drawImage(sdr(frame), 0, 0, rw, rh);
    ctx.restore();
}

/* Как draw(), но с вырезкой: sx/sy/sw/sh задаются в DISPLAY-координатах кадра —
   в тех, которые пользователь видит и в которых он выбирал область. Пересчёт в
   coded-координаты — забота этого файла, а не тула. */
function drawSource(ctx, frame, sx, sy, sw, sh, dx, dy, dw, dh) {
    if (!ctx || !frame) return;
    var d = pending(frame);
    if (!d) { ctx.drawImage(sdr(frame), sx, sy, sw, sh, dx, dy, dw, dh); return; }
    var disp = size(frame);
    var cw = (d === 180) ? disp.w : disp.h;   // coded-ширина
    var ch = (d === 180) ? disp.h : disp.w;   // coded-высота
    var csx, csy, csw, csh;
    if (d === 90)       { csx = sy;           csy = ch - sx - sw; csw = sh; csh = sw; }
    else if (d === 180) { csx = cw - sx - sw; csy = ch - sy - sh; csw = sw; csh = sh; }
    else                { csx = cw - sy - sh; csy = sx;           csw = sh; csh = sw; }
    ctx.save();
    ctx.translate(dx, dy);
    if (d === 90) { ctx.translate(dw, 0); ctx.rotate(Math.PI / 2); }
    else if (d === 180) { ctx.translate(dw, dh); ctx.rotate(Math.PI); }
    else { ctx.translate(0, dh); ctx.rotate(-Math.PI / 2); }
    var rw = (d === 180) ? dw : dh, rh = (d === 180) ? dh : dw;
    ctx.drawImage(sdr(frame), csx, csy, csw, csh, 0, 0, rw, rh);
    ctx.restore();
}

/* Кадр, чей поворот ещё «едет рядом», — в кадр, который ПРАВ САМ: пиксели
   повёрнуты, displayWidth/Height — display-размер, pending() — 0. Нужен там,
   где кадр уходит к потребителю, который рисует его обычным drawImage, —
   а таких большинство: слайдшоу, наложения, клоны рисуют источник своими
   общими функциями для фото и видео сразу (обращение 19.10.2026, Firefox:
   «в колоде 9:16 последние видео сохранились в формате 16:9» — клип с
   телефона ложился в файл боком и вписывался полосой).
   Цена — одна отрисовка кадра в холст: Firefox 148, 1080p rot90 —
   16 мс/кадр у VideoOrientation.draw против 34 мс у копии с той же
   отрисовкой. Поэтому копия делается только у кадра с неприменённым
   поворотом (Chromium несёт поворот в самом кадре и сюда не доходит) и
   только при выдаче потребителю, а не при декодировании: очередь лейна
   держит до 26 кадров, и RGBA-копия 1080×1920 (8 МБ) против I420 (3 МБ)
   утроила бы её. Потребитель, рисующий через draw/drawSource/size, копию
   может не просить — см. `deferRotation` у DecodeStream.
   Холст один на модуль: new VideoFrame(canvas) снимает пиксели сразу. */
var _upCanvas = null;
function upright(frame) {
    var d = pending(frame);
    if (!frame || !d) return frame;
    try {
        var s = size(frame);
        if (!s.w || !s.h) return frame;
        if (!_upCanvas) _upCanvas = (typeof OffscreenCanvas !== 'undefined')
            ? new OffscreenCanvas(s.w, s.h) : document.createElement('canvas');
        if (_upCanvas.width !== s.w) _upCanvas.width = s.w;
        if (_upCanvas.height !== s.h) _upCanvas.height = s.h;
        draw(_upCanvas.getContext('2d'), frame, 0, 0, s.w, s.h);
        var init = { timestamp: frame.timestamp };
        if (frame.duration) init.duration = frame.duration;
        var out = new VideoFrame(_upCanvas, init);
        try { frame.close(); } catch (e) {}
        return out;
    } catch (e) {
        /* Копия не вышла — кадр остаётся с поворотом «рядом»: потребитель,
           знающий draw(), нарисует его верно, остальные — как раньше. */
        return frame;
    }
}

window.VideoOrientation = {
    fromTkhd: fromTkhd,
    display: display,
    native: native,
    apply: apply,
    pending: pending,
    size: size,
    draw: draw,
    drawSource: drawSource,
    upright: upright,
};
})();
